<script setup lang="ts">
import gsap from 'gsap'
import type { SectionCanvasXpRecord } from '~/types/datocms'
import { useProgress } from '@tresjs/cientos'
import { EffectComposerPmndrs, VignettePmndrs } from '@tresjs/post-processing'
// import { BlendFunction } from 'postprocessing'
import { getMousePos } from '~/utils'
import { queryClients } from '~/graphql/clients'

const { data: dataClients, error } = await useDatoQuery(queryClients)

if (error.value) {
  console.error(error.value)
}

const cameraRef = shallowRef()

defineProps<{
  data: SectionCanvasXpRecord
  enabled: boolean
}>()

const options = {
  shadows: false,
  alpha: false,
  clearColor: 0x000000,
  powerPreference: 'high-performance',
  antialias: false,
  stencil: false,
  depth: false
}

const cameraPosition = ref({ x: 0, y: 0, z: 2 })

function onMouseMove(e: MouseEvent) {
  const canvasContainer = document.querySelector('#clientsCanvasBox') as HTMLElement

  if (!canvasContainer) return

  const rect = canvasContainer.getBoundingClientRect()
  const isMouseInside =
    e.clientX >= rect.left &&
    e.clientX <= rect.right &&
    e.clientY >= rect.top &&
    e.clientY <= rect.bottom

  const coords = getMousePos(e)

  const maxMovementX = 25
  const maxMovementY = 15

  if (!isMouseInside) {
    return
  }

  // Calculate the new camera position based on the mouse position
  let movementX = 0
  let movementY = 0

  if (coords.x > window.innerWidth / 2) {
    movementX = (coords.x - window.innerWidth / 2) / 25
  } else if (coords.x < window.innerWidth / 2) {
    movementX = -(window.innerWidth / 2 - coords.x) / 25
  }

  if (coords.y > window.innerHeight / 2) {
    movementY = -(coords.y - window.innerHeight / 2) / 30
  } else if (coords.y < window.innerHeight / 2) {
    movementY = (window.innerHeight / 2 - coords.y) / 30
  }

  // Clamp the movement within the max bounds
  const clampedX = Math.max(-maxMovementX, Math.min(maxMovementX, movementX))
  const clampedY = Math.max(-maxMovementY, Math.min(maxMovementY, movementY))

  // Smoothly move the camera to the new position
  gsap.to(cameraPosition.value, {
    x: clampedX,
    y: clampedY,
    ease: 'power3.out',
    duration: 0.5 // Smooth transition duration
  })
}

const appConfig = useAppConfig()
const isUnveiled = ref(false)
const { hasFinishLoading, progress } = await useProgress()

onMounted(() => {
  if (!appConfig.ui.enable3D) {
    isUnveiled.value = true
  }
  nextTick(() => {
    document.addEventListener('mousemove', onMouseMove)
  })
})

watch(
  hasFinishLoading,
  () => {
    isUnveiled.value = true
  },
  { once: true }
)

onUnmounted(() => {
  document.removeEventListener('mousemove', onMouseMove)
})
</script>

<template>
  <div id="clientsCanvasBox" class="h-screen w-screen">
    <Transition
      name="fade-overlay"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
      enter-active-class="transition-opacity duration-500"
      leave-active-class="transition-opacity duration-500"
    >
      <CanvasLoading v-if="!isUnveiled" :progress="progress" />
    </Transition>
    <TresCanvas v-bind="options" class="absolute left-0 top-0 h-full w-full">
      <TresAmbientLight :intensity="20" />
      <TresPerspectiveCamera
        :ref="cameraRef"
        :look-at="[0, 0, 0]"
        :position="[cameraPosition.x, cameraPosition.y, -12]"
      >
        <TresPointLight :intensity="5000" :position="[0, 0, -30]" :color="0x555555" />
      </TresPerspectiveCamera>
      <Plane :args="[100, 100]" :rotation="[0, 0, 0]" :position="[0, 0, 2]">
        <TresMeshStandardMaterial color="black" :emissive="0x2a142f" :emissive-intensity="0.5" />
      </Plane>

      <Suspense>
        <CanvasClientsGrid ref="clientsRef" :data="dataClients?.allClients" />
      </Suspense>

      <EffectComposerPmndrs>
        <VignettePmndrs :darkness="0.9" :offset="0.5" />
        <!-- 
        <Noise premultiply :opacity="0.1" :blend-function="BlendFunction.SCREEN" />
        -->
      </EffectComposerPmndrs>
    </TresCanvas>
    <div
      class="container page-pt absolute left-0 top-0 flex h-full w-full flex-col gap-4 lg:justify-between"
    >
      <Heading v-if="data.title && isUnveiled" :size="1"
        ><TextReveal v-if="isUnveiled" :delay="1">{{ data.title }}</TextReveal></Heading
      >
      <div v-if="data.subtitle && isUnveiled" class="mb-20 max-w-[18rem] lg:leading-[1.75]">
        <TextReveal v-if="isUnveiled" :delay="1.5">{{ data.subtitle }}</TextReveal>
      </div>
    </div>
  </div>
</template>
